import { isBefore } from "date-fns"

import { Item, ItemStatus, ItemType } from "@/models/Item"
import { ChartInfo, Project } from "@/models/Project"
import {
  differenceInBusinessDays,
  formatDate,
  getTodayCalendarDate,
  toAllDayISOString,
  toCalendarDate
} from "@/utils/dateUtil"

import { BugMetrics, PlatformLoadMetrics, TeamBacklogsMetrics } from "./BacklogMetrics"
import { Base } from "./Base"
import { HealthCheckSummary } from "./HealthCheck"
import { RetroStatus } from "./Retrospective"
import { RoleStats, Team } from "./Team"
import { User } from "./User"

export const SortedHealthCheckTrends = ["HEALTHY", "NEUTRAL", "UNHEALTHY", "UNAVAILABLE"]

export const ItemStatusesCycleTime: ItemStatus[] = [
  "IN_DISCOVERY",
  "DISCOVERY_DONE",
  "IN_UX",
  "UX_DONE",
  "IN_UI_DESIGN",
  "UI_DESIGN_DONE",
  "TESTING",
  "UI_TESTING_DONE",
  "DELIVERY",
  "DELIVERY_DONE",
  "DEV_HANDOFF",
  "HANDOFF_DONE",
  "INFRASTRUCTURE",
  "INFRASTRUCTURE_DONE",
  "CI_CD_PIPELINE",
  "PIPELINE_DONE",
  "CLIENT_REVIEW",
  "PM_REVIEW",
  "IN_RESEARCH",
  "RESEARCH_DONE",
  "CONTENT_CREATING",
  "CONTENT_DONE",
  "IN_GRAPHIC_DESIGN",
  "GRAPHIC_DESIGN_DONE",
  "IN_UI_UX",
  "UI_UX_DONE",
  "IN_SEO",
  "SEO_DONE",
  "IN_SMM",
  "SMM_DONE",
  "IN_DESIGN",
  "DESIGN_COMPLETED",
  "IN_DEVELOPMENT",
  "DEVELOPMENT_DONE",
  "SECURITY",
  "SECURITY_DONE",
  "MITIGATION",
  "MITIGATION_DONE",
  "REMEDIATING",
  "REMEDIATION_DONE",
  "IN_TESTING",
  "TESTING_DONE",
  "IN_REVIEW",
  "REVIEW_DONE",
  "OBSERVABILITY",
  "OBSERVABILITY_DONE",
  "ALERTING",
  "ALERTING_DONE",
  "PERFORMANCE",
  "PERFORMANCE_DONE",
  "COST_OPTIMIZATION",
  "OPTIMIZATION_DONE",
  "IN_OPS",
  "OPS_DONE",
  "IN_QA",
  "TESTING_COMPLETE",
  "DOCUMENTATION",
  "DOCUMENTATION_DONE",
  "BLOCKED"
]

export type SprintStatus = "NOT_STARTED" | "STARTED" | "HALTED" | "COMPLETED"

export type SprintSyncErrorType =
  | "START_EMPTY_SPRINT"
  | "SPRINT_DECOMPOSITION_REQUIRED"
  | "ACTIVE_SPRINT_EXISTS"
  | "SPRINT_DATES_OVERLAP"

export type SprintStatusReview = "IN_REVIEW" | "REVIEW_COMPLETED" | "NOT_STARTED" | "COMPLETED"

export type SelectedChart = "BURNDOWN" | "HISTORY"

export const sortedSprintIntervalsTypes: SprintIntervalsTypes[] = [
  "CURRENT_SPRINT",
  "THREE_SPRINTS",
  "SIX_SPRINTS"
]

export type SprintIntervalsTypes = "CURRENT_SPRINT" | "THREE_SPRINTS" | "SIX_SPRINTS"

export interface Quote {
  message: string
  author: string
}

export interface TimeOff {
  description: string
  userId: string
  startDate: string
  endDate: string
}

export class ScheduleTimeOff {
  id?: string = undefined
  startDate: Date = getTodayCalendarDate()
  endDate: Date = getTodayCalendarDate()
  members: User[] = []
  description = ""

  constructor(props?: Partial<ScheduleTimeOff>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface AddItemSprintPayload {
  sprintId: string
  itemId: string
}

export interface SprintPreview {
  continuousSprint: boolean
  id: string
  sequenceNumber: number
  status: SprintStatus
  teamId: string
}

export interface SprintInfo {
  id: string
  sequenceNumber: number
  endDate: string
  startDate: string
  status: SprintStatus
  zeroSprint?: boolean
  continuousSprint?: boolean
  wipLimitExceeded?: boolean
  wip?: number
  goals?: string
  syncErrorType?: SprintSyncErrorType
}

export class SprintMetrics {
  capacityHours = 0
  items = 0
  totalItems = 0
  notEstimated = 0
  remainingHours = 0
  plannedEffort = 0
  totalStoryPoints = 0
  timeLeftHours = 0
  completedStoryPoints = 0
  unassigned = 0
  velocity = 0
  remaining = 0
  completed = 0
  chartInfo?: ChartInfo
  itemMetricsLog: SprintMetricsLog = {}
  currentRiskPercent = 0
  inProgress = 0
  notStarted = 0
  avgCycleTime = 0
  avgLeadTime = 0
  avgWip = 0
  wipLimitExceeded = false
  movedToAnotherTeam = 0

  constructor(props?: Partial<SprintMetrics>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface SprintMetricsLog {
  [key: string]: SprintMetricsLogData
}
export interface SprintMetricsLogData {
  notStarted: number
  inProgress: number
  completed: number
  movedToAnotherTeam: number
}

export interface SprintCreationContext {
  existsActiveContinuousSprint: boolean
  sprintCapacity: SprintCapacity
  nextSequenceNumber: number
  team: Team
  startDate: string
  endDate: string
}

export interface SprintWithStats {
  sprint: Sprint
  stats: SprintMetrics
}

export interface WaitingMetrics {
  blockedItemsCount: number
  pmReviewItemsCount: number
  clientReviewItemsCount: number
  totalWaitTimeInHours: number
}

export interface WaitingMetricView {
  id: keyof WaitingMetrics
  label: string
}

export const waitingMetricsLevelsParsed: WaitingMetricView[] = [
  {
    id: "blockedItemsCount",
    label: "Blocked"
  },
  {
    id: "pmReviewItemsCount",
    label: "PM Review"
  },
  {
    id: "clientReviewItemsCount",
    label: "Client Review"
  }
]

export class SprintWithStatsExtended {
  bugMetrics: BugMetrics = new BugMetrics()
  sprint: Sprint = new Sprint()
  stats: SprintMetrics = new SprintMetrics()
  bugMetricsByProjects: { [key: string]: BugMetrics } = {}
  waitingByProjects: { [key: string]: WaitingMetrics } = {}
  platformLoadByProjects: { [key: string]: PlatformLoadMetrics } = {}
  projects: Project[] = []
  teamBacklogsMetricsByProject: { [key: string]: TeamBacklogsMetrics } = {}
  favorite = false

  constructor(props?: Partial<SprintWithStatsExtended>) {
    if (!props) return
    this.bugMetrics = new BugMetrics(props.bugMetrics)
    this.sprint = new Sprint(props.sprint)
    this.stats = new SprintMetrics(props.stats)
    this.bugMetricsByProjects = props.bugMetricsByProjects ?? {}
    this.waitingByProjects = props.waitingByProjects ?? {}
    this.platformLoadByProjects = props.platformLoadByProjects ?? {}
    this.teamBacklogsMetricsByProject = props.teamBacklogsMetricsByProject ?? {}
    this.projects = props.projects ? props.projects.map(e => new Project(e)) : []
    this.favorite = props.favorite ?? false
  }
}

/** Dates the user picked are `Date`s and become UTC midnight; server strings are sent back untouched. */
const toCalendarDatePayload = (value: Date | string) =>
  typeof value === "string" ? value : toAllDayISOString(toCalendarDate(value))

export class Sprint extends Base<Sprint> {
  id = ""
  status: SprintStatus = "NOT_STARTED"
  startDate = ""
  endDate = ""
  continueEndDate = ""
  utilizationRatio = 65
  timeOffs: TimeOff[] = []
  projectId = ""
  team: Team = new Team()
  sequenceNumber = 1
  storyPoints = 0
  teamId = ""
  previousSprintId = ""
  nextSprintId = ""
  goals = ""
  summary = ""
  haltReason = ""
  retrospectiveStatus: RetroStatus = "NOT_STARTED"
  zeroSprint = false
  continuousSprint = false
  wipLimitExceeded = false
  wip = 1
  continuousCycle = 0
  size = 0
  requiredRetrospective = true
  nextSprintUpdated = false
  reviewStatus: SprintStatusReview = "NOT_STARTED"
  externalSprintName: string
  syncErrorType?: SprintSyncErrorType
  historicalImport: boolean

  constructor(props?: Partial<Sprint>) {
    super()
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    if (this.continuousSprint && this.continueEndDate) {
      this.endDate = this.continueEndDate
    }
    this.team = new Team(props.team)
  }

  getJsonObj() {
    return {
      teamId: this.teamId,
      status: this.status ?? "NOT_STARTED",
      startDate: toCalendarDatePayload(this.startDate),
      endDate: toCalendarDatePayload(this.endDate),
      continueEndDate: this.continueEndDate ? toCalendarDatePayload(this.continueEndDate) : "",
      utilizationRatio: this.utilizationRatio,
      timeOffs: this.timeOffs.map(timeOff => ({
        ...timeOff,
        startDate: toCalendarDatePayload(timeOff.startDate),
        endDate: toCalendarDatePayload(timeOff.endDate)
      })),
      goals: this.goals,
      summary: this.summary,
      haltReason: this.haltReason,
      zeroSprint: this.zeroSprint,
      continuousSprint: this.continuousSprint,
      wip: this.wip > 0 ? this.wip : 1,
      continuousCycle: this.continuousCycle,
      requiredRetrospective: this.requiredRetrospective,
      reviewStatus: this.reviewStatus,
      externalSprintName: this.externalSprintName
    }
  }

  static create(props?: Partial<Sprint>): Sprint {
    return new Sprint(props)
  }

  get isStarted() {
    return this.status === "STARTED"
  }

  get isCompleted() {
    return this.status === "COMPLETED"
  }

  get isHalted() {
    return this.status === "HALTED"
  }

  get isNotStarted() {
    return this.status === "NOT_STARTED"
  }

  get isReview() {
    return this.reviewStatus === "IN_REVIEW"
  }

  get isReviewCompleted() {
    return this.reviewStatus === "REVIEW_COMPLETED"
  }

  get retroIsCompleted() {
    return this.retrospectiveStatus === "COMPLETED"
  }

  get retroIsNotStarted() {
    return this.retrospectiveStatus === "NOT_STARTED"
  }

  get retroIsRequested() {
    return this.retrospectiveStatus === "REQUESTED"
  }

  get retroIsRevealed() {
    return this.retrospectiveStatus === "REVEALED"
  }

  get retroIsStarted() {
    return this.retrospectiveStatus === "STARTED"
  }

  get formatedDateStart() {
    return this.startDate ? formatDate(this.startDate, "dd MMM", { dateOnly: true }) : ""
  }

  get formatedDateEnd() {
    return this.endDate ? formatDate(this.endDate, "dd MMM", { dateOnly: true }) : ""
  }

  get workDays() {
    return differenceInBusinessDays(toCalendarDate(this.endDate), toCalendarDate(this.startDate))
  }

  get canCompleteSprint() {
    return !isBefore(getTodayCalendarDate(), toCalendarDate(this.endDate))
  }

  get changeByFields() {
    return [
      "startDate",
      "endDate",
      "utilizationRatio",
      "continuousSprint",
      "wip",
      "goals",
      "requiredRetrospective",
      "continuousCycle",
      "continueEndDate"
    ] as (keyof Sprint)[]
  }

  onWipLimitsCalculate(numberOfUsers: number) {
    this.wip = numberOfUsers + Math.round(numberOfUsers / 2) + 1
  }

  getComplitionBarConfig(stats: SprintMetrics) {
    const percentCompletion = stats["items"]
      ? Math.round((stats["completed"] / stats["items"]) * 100)
      : 0
    return {
      label: `Completion ${percentCompletion}%`,
      currentValue: percentCompletion,
      maxValue: 100
    }
  }

  isModified(keys?: (keyof Sprint)[], inverse?: boolean) {
    let isModified = false
    if (!isModified) {
      isModified = super.isModified(keys, inverse)
    }
    return isModified
  }
}

export interface SprintCapacityFilter {
  endDate: string
  startDate: string
  teamId: string
  utilizationRatio: number
  timeOffs: TimeOff[]
}

export class SprintCapacity {
  capacityHours = 0
  developerDays = 0
  workDays = 0

  constructor(props?: Partial<SprintCapacity>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class DataByBacklogType {
  storyAndSpike: number
  bug: number
  incident: number
  techDebt: number
  designDebt: number
  supportCase: number
  devOps: number
  total: number

  constructor(props?: Partial<DataByBacklogType>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

interface KeyWithNumber {
  [key: string]: number
}

export class DataByItemType {
  story = 0
  spike = 0
  product = 0
  bug = 0
  incident = 0
  techDebt = 0
  designDebt = 0
  supportCase = 0
  devOps = 0
  total = 0

  constructor(props?: Partial<DataByItemType>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class DataByTaskType {
  design = 0
  uxDesign = 0
  developer = 0
  qa = 0
  bug = 0
  devops = 0
  metrics: []

  constructor(props?: Partial<DataByTaskType>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export type ItemTypeMetrics = { [K in ItemType]?: number }
export type ItemTypeMetricsByKey = { [K in ItemType]?: number }

export interface QaTileLine {
  [key: string]: {
    hours: number
    items: Item[]
  }
}

export class SprintIntervals {
  CURRENT_SPRINT = 0
  THREE_SPRINTS = 0
  SIX_SPRINTS = 0

  constructor(props?: Partial<SprintIntervals>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class ItemTypesBySprintIntervals {
  CURRENT_SPRINT: ItemTypeMetrics = {}
  THREE_SPRINTS: ItemTypeMetrics = {}
  SIX_SPRINTS: ItemTypeMetrics = {}

  constructor(props?: Partial<ItemTypesBySprintIntervals>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class ProjectSprintAnalytics {
  completedItems = 0
  itemsPlanned = 0
  percentCompleted = 0
  avgCompletedItemsByPeriod: SprintIntervals = new SprintIntervals()
  avgStoriesUnblockedWithinInHours: SprintIntervals = new SprintIntervals()
  avgVelocity: SprintIntervals = new SprintIntervals()
  capacityObservationInPercent = 0
  carryOverItems = 0
  carryOverStoryPoints = 0
  avgLeadTimeInDays: ItemTypesBySprintIntervals = new ItemTypesBySprintIntervals()
  avgCycleTimeInDays: ItemTypesBySprintIntervals = new ItemTypesBySprintIntervals()

  constructor(props?: Partial<ProjectSprintAnalytics>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class SprintAnalytics extends ProjectSprintAnalytics {
  sprint = new Sprint()
  projects: Project[] = []
  sprintMainAnalyticsByProject: { [projectId: string]: ProjectSprintAnalytics } = {}

  constructor(props?: Partial<SprintAnalytics>) {
    super(props)
    if (!props) return
    Object.keys(props).forEach(field => {
      if (field === "sprint") {
        this[field] = new Sprint(props[field])
        return
      }

      this[field] = props[field]
    })
  }
}

export interface CycleTimeBySprintIntervals {
  CURRENT_SPRINT: { [key: string]: KeyWithNumber }
  THREE_SPRINTS: { [key: string]: KeyWithNumber }
  SIX_SPRINTS: { [key: string]: KeyWithNumber }
}

export interface GeneralCycleTimeBySprintIntervals {
  CURRENT_SPRINT: KeyWithNumber
  THREE_SPRINTS: KeyWithNumber
  SIX_SPRINTS: KeyWithNumber
}

export class AvgCycleTimeInDaysWithStoryPoints {
  CURRENT_SPRINT: ItemTypeMetricsByKey = {}
  THREE_SPRINTS: ItemTypeMetricsByKey = {}
  SIX_SPRINTS: ItemTypeMetricsByKey = {}

  constructor(props?: Partial<AvgCycleTimeInDaysWithStoryPoints>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class ProjectSprintAnalyticsPerformance {
  avgAmountOfWork: { [key: string]: DataByTaskType } = {}
  backlogAnalytics: ItemTypeMetricsByKey = {}
  avgSprintItemCycleTimeAnalytics: {
    [key: string]: {
      [key: string]: DataByItemType
    }
  } = {}
  avgSprintGeneralCycleTimeByStatus: GeneralCycleTimeBySprintIntervals = {
    CURRENT_SPRINT: {},
    THREE_SPRINTS: {},
    SIX_SPRINTS: {}
  }
  avgSprintCycleTimeByStatus: CycleTimeBySprintIntervals = {
    CURRENT_SPRINT: {},
    THREE_SPRINTS: {},
    SIX_SPRINTS: {}
  }
  avgTasksByItemType: TasksByItemType = new TasksByItemType()
  avgRevertedItemStatusOccurrences: { [key: string]: number } = {}
  avgLeadTimeInDays: ItemTypesBySprintIntervals = new ItemTypesBySprintIntervals()
  avgCycleTimeInDays: AvgCycleTimeInDaysWithStoryPoints = new AvgCycleTimeInDaysWithStoryPoints()
  constructor(props?: Partial<ProjectSprintAnalyticsPerformance>) {
    if (!props) return

    Object.assign(this, props)
    this.processCycleTimes()
  }

  protected processCycleTimes() {
    sortedSprintIntervalsTypes.forEach(e => {
      this.avgSprintCycleTimeByStatus[e] = this.sortAllCycleTimes(
        this.avgSprintCycleTimeByStatus[e]
      )
      this.avgSprintGeneralCycleTimeByStatus[e] = this.sortCycleTime(
        this.avgSprintGeneralCycleTimeByStatus[e]
      )
    })
  }

  protected sortAllCycleTimes(cycleTimes: { [key: string]: any }): any {
    const sorted: any = {}
    for (const key in cycleTimes) {
      sorted[key] = this.sortCycleTime(cycleTimes[key])
    }
    return sorted
  }

  protected sortCycleTime(cycleTime: { [key: string]: number }): { [key: string]: number } {
    const sortedCycleTime: { [key: string]: number } = {}

    if (!cycleTime || !Object.keys(cycleTime).length) return sortedCycleTime

    ItemStatusesCycleTime.forEach(status => {
      if (cycleTime[status]) {
        sortedCycleTime[status] = cycleTime[status]
      }
    })

    return sortedCycleTime
  }
}

export class SprintAnalyticsPerformance extends ProjectSprintAnalyticsPerformance {
  sprintPerformanceAnalyticsByProject: { [projectId: string]: ProjectSprintAnalyticsPerformance } =
    {}

  constructor(props?: Partial<SprintAnalyticsPerformance>) {
    super(props)
    if (!props) return

    Object.assign(this, props)
    this.processPerformanceAnalytics()
  }

  protected processPerformanceAnalytics() {
    this.processCycleTimes()

    if (this.sprintPerformanceAnalyticsByProject) {
      for (const projectId in this.sprintPerformanceAnalyticsByProject) {
        const projectAnalytics = this.sprintPerformanceAnalyticsByProject[projectId]
        if (projectAnalytics) {
          this.sprintPerformanceAnalyticsByProject[projectId] =
            new ProjectSprintAnalyticsPerformance(projectAnalytics)
        }
      }
    }
  }
}

export class ProjectSprintAnalyticsQuality {
  totalBugsAdded: SprintIntervals = new SprintIntervals()
  totalTechDebtAdded: SprintIntervals = new SprintIntervals()
  totalDesignDebtAdded: SprintIntervals = new SprintIntervals()
  totalIncidentsAdded: SprintIntervals = new SprintIntervals()
  qaEffortInHours = 0
  readyForQaTimeline: QaTileLine = {}
  avgSupportCaseCycleTimeInHours: SupportCaseCycleTime = new SupportCaseCycleTime()
  avgIncidentsCycleTimeInHours: SupportCaseCycleTime = new SupportCaseCycleTime()
  totalSupportCasesAdded: SprintIntervals = new SprintIntervals()

  constructor(props?: Partial<ProjectSprintAnalyticsQuality>) {
    if (!props) return

    if (props.totalBugsAdded) this.totalBugsAdded = props.totalBugsAdded
    if (props.totalTechDebtAdded) this.totalTechDebtAdded = props.totalTechDebtAdded
    if (props.totalDesignDebtAdded) this.totalDesignDebtAdded = props.totalDesignDebtAdded
    if (props.totalIncidentsAdded) this.totalIncidentsAdded = props.totalIncidentsAdded
    if (props.qaEffortInHours) this.qaEffortInHours = props.qaEffortInHours
    if (props.readyForQaTimeline) this.readyForQaTimeline = props.readyForQaTimeline
    if (props.avgSupportCaseCycleTimeInHours)
      this.avgSupportCaseCycleTimeInHours = new SupportCaseCycleTime(
        props.avgSupportCaseCycleTimeInHours
      )
    if (props.avgIncidentsCycleTimeInHours)
      this.avgIncidentsCycleTimeInHours = new SupportCaseCycleTime(
        props.avgIncidentsCycleTimeInHours
      )
    if (props.totalSupportCasesAdded) this.totalSupportCasesAdded = props.totalSupportCasesAdded
  }
}

export class SprintAnalyticsQuality extends ProjectSprintAnalyticsQuality {
  sprintQualityAnalyticsByProject: { [projectId: string]: ProjectSprintAnalyticsQuality } = {}

  constructor(props?: Partial<SprintAnalyticsQuality>) {
    super(props)
    if (!props) return

    if (props.sprintQualityAnalyticsByProject) {
      this.sprintQualityAnalyticsByProject = Object.entries(
        props.sprintQualityAnalyticsByProject
      ).reduce(
        (acc, [projectId, analytics]) => {
          acc[projectId] = new ProjectSprintAnalyticsQuality(analytics)
          return acc
        },
        {} as { [projectId: string]: ProjectSprintAnalyticsQuality }
      )
    }
  }
}

export class SupportCaseCycleTimeTypes {
  CRITICAL = 0
  HIGH = 0
  MEDIUM = 0
  TRIVIAL = 0

  constructor(props?: Partial<SupportCaseCycleTimeTypes>) {
    if (!props) return

    this.CRITICAL = props.CRITICAL ?? this.CRITICAL
    this.HIGH = props.HIGH ?? this.HIGH
    this.MEDIUM = props.MEDIUM ?? this.MEDIUM
    this.TRIVIAL = props.TRIVIAL ?? this.TRIVIAL
  }
}

export class SupportCaseCycleTime {
  CURRENT_SPRINT = new SupportCaseCycleTimeTypes()
  THREE_SPRINTS = new SupportCaseCycleTimeTypes()
  SIX_SPRINTS = new SupportCaseCycleTimeTypes()

  constructor(props?: Partial<SupportCaseCycleTime>) {
    if (!props) return

    this.CURRENT_SPRINT = new SupportCaseCycleTimeTypes(props.CURRENT_SPRINT)
    this.THREE_SPRINTS = new SupportCaseCycleTimeTypes(props.THREE_SPRINTS)
    this.SIX_SPRINTS = new SupportCaseCycleTimeTypes(props.SIX_SPRINTS)
  }
}

export class DiscoveredWorkItem {
  avgCumulativeDiscoveredWork = 0
  avgDiscoveredWorkByItemType: ItemTypesBySprintIntervals = new ItemTypesBySprintIntervals()

  constructor(props?: Partial<DiscoveredWorkItem>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.avgDiscoveredWorkByItemType = new ItemTypesBySprintIntervals(
      this.avgDiscoveredWorkByItemType
    )
  }
}

export class DiscoveredWorkSprint {
  CURRENT_SPRINT: DiscoveredWorkItem = new DiscoveredWorkItem()
  THREE_SPRINTS: DiscoveredWorkItem = new DiscoveredWorkItem()
  SIX_SPRINTS: DiscoveredWorkItem = new DiscoveredWorkItem()

  constructor(props?: Partial<DiscoveredWorkSprint>) {
    if (!props) return
    this.CURRENT_SPRINT = new DiscoveredWorkItem(props.CURRENT_SPRINT)
    this.THREE_SPRINTS = new DiscoveredWorkItem(props.THREE_SPRINTS)
    this.SIX_SPRINTS = new DiscoveredWorkItem(props.SIX_SPRINTS)
  }
}

export class ProjectSprintOverviewAnalytics {
  carriedForwardItems: number = 0
  carriedForwardTimeSpent: number = 0
  avgWorkInProgress: SprintIntervals = new SprintIntervals()
  ItemTypesBySprintIntervals: ItemTypesBySprintIntervals = new ItemTypesBySprintIntervals()
  discoveredWork: DiscoveredWorkSprint = new DiscoveredWorkSprint()

  constructor(props?: Partial<ProjectSprintOverviewAnalytics>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.discoveredWork = new DiscoveredWorkSprint(this.discoveredWork)
  }
}

export class SprintAnalyticsOverview extends ProjectSprintOverviewAnalytics {
  healthCheckSummary: HealthCheckSummary = new HealthCheckSummary()
  membersByRole: KeyWithNumber = {}
  team: Team = new Team()
  avgTodosCompleted: SprintIntervals = new SprintIntervals()
  avgTodosDone: SprintIntervals = new SprintIntervals()

  sprintOverviewAnalyticsByProject: { [projectId: string]: ProjectSprintOverviewAnalytics } = {}
  roleStats: RoleStats[] = []
  percentageCompletedItemsByType: ItemTypesBySprintIntervals = new ItemTypesBySprintIntervals()

  constructor(props?: Partial<SprintAnalyticsOverview>) {
    super(props)
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.ItemTypesBySprintIntervals = new ItemTypesBySprintIntervals(
      props.ItemTypesBySprintIntervals
    )
    this.healthCheckSummary = new HealthCheckSummary(this.healthCheckSummary)
    this.team = new Team(this.team)
  }
}

export interface HealthCheckTrendItem {
  UNHEALTHY: number
  NEUTRAL: number
  UNAVAILABLE: number
  HEALTHY: number
}

export class HealthCheckTrends {
  IS_WORK_SATISFYING: { [key: string]: HealthCheckTrendItem } = {}
  SPRINT_HAPPINESS: { [key: string]: HealthCheckTrendItem } = {}
  WORK_LIFE_BALANCE: { [key: string]: HealthCheckTrendItem } = {}
  ATTITUDE: { [key: string]: HealthCheckTrendItem } = {}

  constructor(props?: Partial<HealthCheckTrends>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class SprintAnalyticsHealthCheck {
  healthCheckSummary: HealthCheckSummary = new HealthCheckSummary()
  healthCheckTrends: HealthCheckTrends = new HealthCheckTrends()

  constructor(props?: Partial<SprintAnalyticsHealthCheck>) {
    if (!props) return
    this.healthCheckSummary = new HealthCheckSummary(props.healthCheckSummary)
    this.healthCheckTrends = new HealthCheckTrends(props.healthCheckTrends)
  }
}

export interface TeamInsightMetricsByType {
  [key: string]: number
}

export class TeamInsightMember {
  sprintCapacity = 0
  overlappedSprintsCapacity = 0
  completedItemsByType: TeamInsightMetricsByType = {}
  completedTasksByType: TeamInsightMetricsByType = {}
  timeLeftTrends: string[] = []
  user: User = new User()
  workShare = 0

  constructor(props?: Partial<TeamInsightMember>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.user = new User(this.user)
  }
}

export class ProjectTeamInsights {
  all: TeamInsightMember = new TeamInsightMember()
  members: TeamInsightMember[] = []
  totalItems = 0
  totalItemsCompleted = 0
  totalTasksCompleted = 0

  constructor(props?: Partial<ProjectTeamInsights>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.all = new TeamInsightMember(this.all)
    this.members = (this.members ?? []).map(member => new TeamInsightMember(member))
  }
}

export class TeamInsights {
  team: Team = new Team()
  teamInsightsByProject: { [projectId: string]: ProjectTeamInsights } = {}
  teamInsightsAllProjects: ProjectTeamInsights = new ProjectTeamInsights()

  constructor(props?: Partial<TeamInsights>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.team = new Team(this.team)
    this.teamInsightsByProject = Object.entries(this.teamInsightsByProject).reduce(
      (acc, [projectId, insights]) => {
        acc[projectId] = new ProjectTeamInsights(insights)
        return acc
      },
      {} as { [projectId: string]: ProjectTeamInsights }
    )
    this.teamInsightsAllProjects = new ProjectTeamInsights(this.teamInsightsAllProjects)
  }
}

export interface TasksByItemTypeMetric {
  avg: number
  max: number
  min: number
}

export class TasksByItemType {
  CURRENT_SPRINT: { [key: string]: TasksByItemTypeMetric } = {}
  THREE_SPRINTS: { [key: string]: TasksByItemTypeMetric } = {}
  SIX_SPRINTS: { [key: string]: TasksByItemTypeMetric } = {}

  constructor(props?: Partial<TasksByItemType>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class RevertedStatusCount {
  CURRENT_SPRINT: { [key: string]: number } = {}
  THREE_SPRINTS: { [key: string]: number } = {}
  SIX_SPRINTS: { [key: string]: number } = {}

  constructor(props?: Partial<RevertedStatusCount>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}
